import * as React from "react";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { SecurityAppSecurityEventsHistogram } from "@graylog/enterprise-api";
import FetchErrorAlert from "components/common/FetchErrorAlert";
import Spinner from "components/common/Spinner";
import useUserDateTime from "hooks/useUserDateTime";
import { categoryKey } from "security-app/components/SecurityEvents/Alerts/Constants";
import { parseSearchParams } from "security-app/hooks/api/securityEventsAPI";
import useOnRefresh from "components/common/PaginatedEntityTable/useOnRefresh";
import Histogram, { otherBucket } from "security-app/components/SecurityEvents/Alerts/MitreCategories/Histogram";
import { getPathnameWithoutId } from "util/URLUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
const fetchTechniquesHistogram = async (searchParams, timezone) => {
  const result = await SecurityAppSecurityEventsHistogram.techniquesHistogram(
    parseSearchParams(searchParams, timezone)
  );
  return Object.fromEntries(result.techniques.map((technique) => [technique.id, technique]));
};
const generateChart = (buckets) => {
  const sortedTechniques = Object.values(buckets).sort((b1, b2) => b2.count - b1.count);
  const x = sortedTechniques.map((technique) => technique.id);
  const text = sortedTechniques.map((technique) => technique.technique);
  const y = sortedTechniques.map((technique) => technique.count);
  return { x, y, text };
};
const EventsTechniquesGraph = ({ data, onClickMarker }) => {
  const { x, y, text } = useMemo(() => generateChart(data), [data]);
  return /* @__PURE__ */ React.createElement(Histogram, { onClickMarker, x, y, text, type: "Techniques" });
};
const EventsTechniquesHistogram = ({ searchParams, setFilters }) => {
  const { userTimezone } = useUserDateTime();
  const {
    data,
    isInitialLoading: isLoadingHistogram,
    refetch,
    isError,
    error
  } = useQuery({
    queryKey: ["events", "techniques", searchParams, userTimezone],
    queryFn: () => fetchTechniquesHistogram(searchParams, userTimezone)
  });
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  useOnRefresh(refetch);
  const onClickMarker = useCallback(
    ({ x }) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.SECURITY_EVENTS.MITRE_ATTACK_WIDGET.DRILLDOWN, {
        app_section: "security-events",
        app_pathname: getPathnameWithoutId(pathname),
        app_action_value: { drilldown_into: "technique" }
      });
      if (x !== otherBucket[0]) {
        setFilters(searchParams.filters.set(categoryKey, [x]));
      }
    },
    [pathname, searchParams.filters, sendTelemetry, setFilters]
  );
  if (isLoadingHistogram) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (isError || !data) {
    return /* @__PURE__ */ React.createElement(FetchErrorAlert, { message: "Fetching techniques histogram failed", error });
  }
  return /* @__PURE__ */ React.createElement(EventsTechniquesGraph, { data, onClickMarker });
};
export default EventsTechniquesHistogram;
